import { z, ZodEffects } from "zod";
import { SelectedFeedFormTabCtx } from "../contexts/SelectedFeedFormTabCtx";
import { SelectedPageEditFormTabCtx } from "../contexts/SelectedPageEditFormTabCtx";

export const createFormFieldHandler = (
  formSchema: z.ZodObject<any> | z.ZodEffects<z.ZodObject<any>>,
  formElement: HTMLFormElement,
  isAllowShake: boolean = false,
  isDisplayErrorIndicatorOnBlur: boolean = false
) => {
  type FormData = z.infer<typeof formSchema>;

  const schema = formSchema instanceof ZodEffects ? formSchema.innerType() : formSchema;

  const validateField = (field: keyof FormData, value: string | File | null) => {
    try {
      schema.shape[field].parse(value);
      setFieldError(field, null);
    } catch (error) {
      if (error instanceof z.ZodError) {
        setFieldError(field, error.errors[0].message);
      }
    }
  };

  const setFieldError = (field: keyof FormData, error: string | null) => {
    const errorElement = formElement.querySelector(`.${field.toString()}-error`);
    const fieldElement = formElement.querySelector(`[name="${field.toString()}"]`) as HTMLInputElement | HTMLSelectElement;

    const wrapperElement =
      fieldElement instanceof HTMLSelectElement
        ? formElement.querySelector(`#${field.toString()}-wrapper`)
        : null;

    if (errorElement) {
      errorElement.textContent = error || "";
      errorElement.classList.toggle("hidden", !error);
    }

    // Add error border class to the field's wrapper or toggle element if present
    // used for showing error state on Preline Advance Select element
    if (fieldElement instanceof HTMLSelectElement) {
      if(wrapperElement)
        wrapperElement.classList.toggle("error", !!error);

      const toggleButton = fieldElement.parentElement?.querySelector("button")
      if (toggleButton) {
        // show error border on the preline advance select toggle element
        toggleButton.classList.toggle("border-red-600", !!error); // class for light mode validation
        toggleButton.classList.toggle("dark:border-red-600", !!error); // class for dark mode validation
        toggleButton.classList.toggle("border-stone-200", !error);
      }      
    }
    
    if (fieldElement) {
      fieldElement.classList.toggle("border-red-600", !!error); // class for light mode validation
      fieldElement.classList.toggle("dark:border-red-600", !!error); // class for dark mode validation
      fieldElement.classList.toggle("border-stone-200", !error);
      if(error && isAllowShake && (SelectedFeedFormTabCtx.isFormFieldShake || SelectedPageEditFormTabCtx.isPageFieldShake)) {
        fieldElement.classList.add('animate-shake');
        fieldElement.focus();
          setTimeout(function () {
            SelectedFeedFormTabCtx.isFormFieldShake = false;
            SelectedPageEditFormTabCtx.isPageFieldShake = false;
            fieldElement.classList.remove('animate-shake');
        }, 2000);
      }
    }
  };

  const validateOnBlur = () => {
    Object.keys(schema.shape).forEach((field) => {
      if (formElement) {
        const element = formElement.elements.namedItem(field);
        if (element instanceof HTMLElement) {

          const eventType = (() => {
            switch (true) {
              case element instanceof HTMLSelectElement:
                return "change";
              case element instanceof HTMLInputElement && element.type === "file":
                return "change";
              default:
                return "blur";
            }
          })();

          // add event listner to detect change in the field
          element.addEventListener(eventType, () => {
            // Skip validation if the field is empty
            if (
              element instanceof HTMLSelectElement ||
              element instanceof HTMLInputElement ||
              element instanceof HTMLTextAreaElement
            ) {
              if (element.value === "") {
                setFieldError(field, (isDisplayErrorIndicatorOnBlur ? 'true' : null)); // remove previous error state too
                return;
              }
            }

            const value = (() => {
              switch (true) {
                case element instanceof HTMLSelectElement:
                  return element.value;
                case element instanceof HTMLInputElement && element.type === "file":
                  return element.files ? element.files[0] : "";
                case element instanceof HTMLInputElement:
                case element instanceof HTMLTextAreaElement:
                  return element.value;
                default:
                  return "";
              }
            })();

            validateField(field as keyof FormData, value);
          });
        }
      }
    });
  };

  const resetErrors = () => {
    // Loop through each form field and reset errors
    Object.keys(schema.shape).forEach((field) => {
      setFieldError(field as keyof FormData, null); // Clear error message
    });
  };

  return { setFieldError, validateField, validateOnBlur, resetErrors };
};
